import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, UserRound } from "lucide-react";
import Button from "../components/ui/Button";
import { Chip, StatusBadge } from "../components/ui/Badge";
import { Alert, EmptyState } from "../components/ui/Feedback";
import { trademarkApi } from "../api/trademarkApi";
import { formatDate } from "../lib/format";
import { useAuth } from "../app/AuthProvider";

const RISK_LABEL = {
  low: "Low risk",
  medium: "Medium risk",
  high: "High risk",
};

export default function HistoryPage() {
  const { isAdmin } = useAuth();
  const [state, setState] = useState({
    loading: true,
    items: [],
    total: 0,
    scope: "mine",
    error: null,
  });
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const data = await trademarkApi.getHistory(page, 20);
      setState({
        loading: false,
        items: data.items || [],
        total: data.total || 0,
        scope: data.scope || "mine",
        error: data.dbOffline
          ? "Search history is switched off because no database is connected."
          : null,
      });
    } catch (err) {
      setState({
        loading: false,
        items: [],
        total: 0,
        scope: "mine",
        error: err.message,
      });
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const [deletingAll, setDeletingAll] = useState(false);

  async function handleDelete(id) {
    await trademarkApi.deleteHistoryItem(id).catch(() => {});
    load();
  }

  async function handleDeleteAll() {
    const message = isAdmin
      ? `Delete all ${state.total} searches from every user? This can't be undone.`
      : `Delete all ${state.total} of your searches? This can't be undone.`;
    if (!window.confirm(message)) return;

    setDeletingAll(true);
    try {
      await trademarkApi.deleteAllHistory();
      setPage(1);
      await load();
    } catch (err) {
      setState((s) => ({ ...s, error: err.message }));
    } finally {
      setDeletingAll(false);
    }
  }

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
              {isAdmin ? "All searches" : "Your searches"}
            </h1>
            {isAdmin && (
              <span className="rounded border border-[#8f5eeb]/30 bg-[#8f5eeb] px-2 py-0.5 text-micro font-semibold text-white">
                Admin view — every user
              </span>
            )}
          </div>
          <p className="mt-1.5 max-w-reading text-sm text-ink-600">
            {isAdmin
              ? "Every search run by every user is kept with the rows it returned, so any of them can be reopened without querying the register again."
              : "Every search you've run is kept with the rows it returned, so you can reopen one without querying the register again."}
          </p>
        </div>

        {state.items.length > 0 && (
          <Button
            variant="danger"
            size="sm"
            icon={Trash2}
            loading={deletingAll}
            onClick={handleDeleteAll}
            className="shrink-0"
          >
            {isAdmin ? "Delete all (every user)" : "Delete all"}
          </Button>
        )}
      </div>

      {state.error && (
        <div className="mt-5">
          <Alert tone="info">{state.error}</Alert>
        </div>
      )}

      {!state.loading && !state.error && state.items.length === 0 && (
        <div className="mt-6">
          <EmptyState
            title="No searches yet"
            action={
              <Link to="/">
                <Button>Run your first search</Button>
              </Link>
            }
          >
            Once you search the register, the query and its results are saved
            here.
          </EmptyState>
        </div>
      )}

      <ul className="mt-6 space-y-2.5">
        {state.items.map((item) => (
          <li
            key={item._id}
            className="flex items-center gap-4 rounded border border-ink-200 bg-white px-4 py-3.5 transition-colors hover:border-ink-300"
          >
            <Link to={`/history/${item._id}`} className="min-w-0 flex-1">
              <p className="truncate font-display text-base font-semibold text-ink-900">
                {item.query?.wordMark}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                {isAdmin && (item.createdByName || item.createdByEmail) && (
                  <Chip className="border-[#8f5eeb]/25 bg-[#8f5eeb] text-[#8f5eeb]-600">
                    <UserRound className="h-3 w-3" aria-hidden="true" />
                    {item.createdByName || item.createdByEmail}
                  </Chip>
                )}
                <Chip>{item.resultsCount} records</Chip>
                <Chip>{(item.source || "").toUpperCase()}</Chip>
                <Chip>{formatDate(item.createdAt)}</Chip>
                {item.aiInsight?.overallRiskLevel &&
                  RISK_LABEL[item.aiInsight.overallRiskLevel] && (
                    <StatusBadge
                      status={RISK_LABEL[item.aiInsight.overallRiskLevel]}
                    />
                  )}
              </div>
            </Link>

            <Button
              variant="danger"
              size="sm"
              icon={Trash2}
              onClick={() => handleDelete(item._id)}
              aria-label={`Delete the saved search for ${item.query?.wordMark}`}
            >
              Delete
            </Button>
          </li>
        ))}
      </ul>

      {state.total > 20 && (
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-xs text-ink-600">
            Page {page} of {Math.ceil(state.total / 20)}
          </span>
          <Button
            variant="secondary"
            disabled={page >= Math.ceil(state.total / 20)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
